/** Refresh token is permanently invalid (IdP: invalid_grant, e.g., reuse detection or expired) */
export class InvalidGrantError extends Error {
  constructor() {
    super('OIDC: invalid_grant');
    this.name = 'InvalidGrantError';
  }
}

/**
 * ID token was not accepted: bad signature, wrong issuer or audience, expired, nonce
 * mismatch — or the JWKS could not be reached to decide either way.
 *
 * "Cannot be checked" and "is invalid" share one error because they share one outcome:
 * the token is never trusted. `reason` carries the detail for the log.
 */
export class IdTokenRejectedError extends Error {
  constructor(reason: string) {
    super(`OIDC: ID token rejected — ${reason}`);
    this.name = 'IdTokenRejectedError';
  }
}

/**
 * Aborts the authenticated requests that are waiting for the token check when the session
 * turns out to be gone. Requests without `AUTHENTICATE` never wait and are unaffected.
 */
export class SessionLostError extends Error {
  constructor() {
    super('Session lost — Request aborted');
    this.name = 'SessionLostError';
  }
}

/**
 * The IdP refused the refresh because the requested scope is wider than what was granted
 * at login — typically after a release added a scope to `AUTH_CONFIG.scope`.
 *
 * Deliberately NOT an `InvalidGrantError`: the refresh token itself is intact, so throwing
 * the user out would be the wrong answer to a mismatch only a fresh login can resolve.
 * The refresh still fails, which is why this is an error and not a warning — it is the
 * caller's problem, not the session's.
 */
export class ScopeRejectedError extends Error {
  constructor(
    readonly requestedScope: string,
    readonly grantedScope: string | null,
  ) {
    super(`OIDC: refresh rejected — requested scope exceeds the granted one`);
    this.name = 'ScopeRejectedError';
  }
}
